import React, { useState, useEffect, useCallback } from 'react';
import { Brain, Plus, RefreshCw, Search, X, Save, Upload, Download, AlertTriangle, BarChart2, Sparkles } from 'lucide-react';
import { adminService } from '../services/adminService';
import { useToast } from '../contexts/ToastContext';
import './Pro.css';

interface ProQuestion {
  id: number;
  question_text: string;
  options: string[];
  correct_answer: string;
  explanation: string | null;
  category: string;
  difficulty: string;
  country_relevance: string;
  status: string;
  times_answered: number;
  times_correct: number;
  confidence_score: number | null;
  topic: string | null;
  updated_at: string;
}

interface ProSession {
  id: number;
  user_id: number;
  category: string;
  status: string;
  current_rung: number;
  tp_earned: number;
  safe_haven_tp: number;
  started_at: string;
  ended_at: string | null;
}

const CATEGORIES = ['science', 'politics', 'football', 'history', 'geography', 'entertainment', 'technology', 'general_knowledge', 'mixed'];
const DIFFICULTIES = ['easy', 'easy_medium', 'medium_hard', 'hard', 'expert'];
const Q_STATUSES = ['draft', 'live', 'review', 'retired'];
const S_STATUSES = ['active', 'banked', 'finished', 'failed', 'expired'];
const COUNTRY_OPTIONS = ['universal', 'ng', 'uk'];
const PAGE_SIZE = 25;

const DIFFICULTY_STYLES: Record<string, { background: string; color: string }> = {
  easy:        { background: '#dcfce7', color: '#15803d' },
  easy_medium: { background: '#ccfbf1', color: '#0f766e' },
  medium_hard: { background: '#fef9c3', color: '#854d0e' },
  hard:        { background: '#fed7aa', color: '#9a3412' },
  expert:      { background: '#fee2e2', color: '#991b1b' },
};

function getConfidenceStyle(score: number | null): { background: string; color: string } {
  if (score === null) return { background: '#f3f4f6', color: '#9ca3af' };
  if (score >= 0.5)   return { background: '#dcfce7', color: '#15803d' };
  if (score >= 0.2)   return { background: '#fef9c3', color: '#854d0e' };
  return               { background: '#fee2e2', color: '#991b1b' };
}

const SESSION_STATUS_COLOURS: Record<string, { background: string; color: string }> = {
  active:   { background: '#dcfce7', color: '#15803d' },
  banked:   { background: '#fef9c3', color: '#854d0e' },
  finished: { background: '#ede9fe', color: '#6d28d9' },
  failed:   { background: '#fee2e2', color: '#991b1b' },
  expired:  { background: '#f3f4f6', color: '#6b7280' },
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function sessionDuration(start: string, end: string | null): string {
  if (!end) return 'Active';
  const secs = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

const EMPTY_FORM = {
  question_text: '',
  options: ['', '', '', ''],
  correct_answer: '',
  category: '',
  difficulty: '',
  country_relevance: 'universal',
  explanation: '',
  topic: '',
};

const Pro: React.FC = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'questions' | 'sessions' | 'import'>('questions');

  // Questions state
  const [questions, setQuestions] = useState<ProQuestion[]>([]);
  const [qTotal, setQTotal] = useState(0);
  const [qOffset, setQOffset] = useState(0);
  const [qLoading, setQLoading] = useState(false);
  const [qFilters, setQFilters] = useState({ category: 'all', difficulty: 'all', status: 'all' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_FORM, options: ['', '', '', ''] });
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [calibrating, setCalibrating] = useState(false);
  const [showGenModal, setShowGenModal] = useState(false);
  const [genForm, setGenForm] = useState({ category: 'general_knowledge', difficulty: 'easy', count: 10, country_relevance: 'universal' });
  const [generating, setGenerating] = useState(false);
  const [showAllTiersModal, setShowAllTiersModal] = useState(false);
  const [allTiersForm, setAllTiersForm] = useState({ category: 'general_knowledge', country_relevance: 'ng', count_per_tier: 10 });
  const [generatingAllTiers, setGeneratingAllTiers] = useState(false);
  const [allTiersProgress, setAllTiersProgress] = useState<string | null>(null);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Sessions state
  const [sessions, setSessions] = useState<ProSession[]>([]);
  const [sTotal, setSTotal] = useState(0);
  const [sOffset, setSOffset] = useState(0);
  const [sLoading, setSLoading] = useState(false);
  const [sFilters, setSFilters] = useState({ status: 'all', user_id: '' });
  const [sPendingFilters, setSPendingFilters] = useState({ status: 'all', user_id: '' });

  // Import state
  const [importJson, setImportJson] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created?: number; errors?: string[] } | null>(null);

  const loadQuestions = useCallback(async (offset = 0, filters = qFilters) => {
    setQLoading(true);
    try {
      const params: Record<string, string | number> = { limit: PAGE_SIZE, offset };
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.difficulty !== 'all') params.difficulty = filters.difficulty;
      if (filters.status !== 'all') params.status = filters.status;
      const data = await adminService.listProQuestions(params);
      setQuestions(data.items);
      setQTotal(data.total);
      setQOffset(offset);
    } catch {
      toast.error('Error', 'Failed to load questions');
    } finally {
      setQLoading(false);
    }
  }, [qFilters, toast]);

  const loadSessions = useCallback(async (offset = 0, filters = sFilters) => {
    setSLoading(true);
    try {
      const params: Record<string, string | number> = { limit: PAGE_SIZE, offset };
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.user_id) params.user_id = Number(filters.user_id);
      const data = await adminService.listProSessions(params);
      setSessions(data.items);
      setSTotal(data.total);
      setSOffset(offset);
    } catch {
      toast.error('Error', 'Failed to load sessions');
    } finally {
      setSLoading(false);
    }
  }, [sFilters, toast]);

  useEffect(() => {
    if (activeTab === 'questions') loadQuestions(0, qFilters);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'sessions') loadSessions(0, sFilters);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQFilterChange = (key: string, value: string) => {
    const next = { ...qFilters, [key]: value };
    setQFilters(next);
    loadQuestions(0, next);
  };

  const handleCreateFormOption = (idx: number, value: string) => {
    const options = [...createForm.options];
    options[idx] = value;
    const correct = options.includes(createForm.correct_answer) ? createForm.correct_answer : '';
    setCreateForm(f => ({ ...f, options, correct_answer: correct }));
  };

  const isCreateValid = () => {
    const { question_text, options, correct_answer, category, difficulty } = createForm;
    return (
      question_text.trim() &&
      options.every(o => o.trim()) &&
      correct_answer &&
      options.includes(correct_answer) &&
      category &&
      difficulty
    );
  };

  const handleCreate = async () => {
    if (!isCreateValid()) return;
    setCreating(true);
    try {
      await adminService.createProQuestion({
        question_text: createForm.question_text.trim(),
        options: createForm.options.map(o => o.trim()),
        correct_answer: createForm.correct_answer,
        category: createForm.category,
        difficulty: createForm.difficulty,
        country_relevance: createForm.country_relevance,
        explanation: createForm.explanation.trim() || undefined,
        topic: createForm.topic.trim() || undefined,
      });
      toast.success('Question Created', 'New question saved as Draft.');
      setShowCreateForm(false);
      setCreateForm({ ...EMPTY_FORM, options: ['', '', '', ''] });
      loadQuestions(qOffset, qFilters);
    } catch {
      toast.error('Error', 'Failed to create question');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    setUpdatingId(id);
    try {
      await adminService.updateProQuestion(id, { status: newStatus });
      setQuestions(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q));
    } catch {
      toast.error('Error', 'Failed to update question status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCalibrate = async () => {
    setCalibrating(true);
    try {
      const result = await adminService.calibrateProQuestions();
      toast.success('Calibration Complete', `${result.updated} updated — ${result.flagged_to_review} flagged, ${result.promoted_to_live} promoted to Live.`);
      loadQuestions(qOffset, qFilters);
    } catch {
      toast.error('Error', 'Calibration failed');
    } finally {
      setCalibrating(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await adminService.generateProQuestions(genForm);
      setShowGenModal(false);
      toast.success('Generated', `${result.inserted} questions added as DRAFT${result.skipped > 0 ? ` (${result.skipped} skipped)` : ''}`);
      loadQuestions(qOffset, qFilters);
    } catch {
      toast.error('Error', 'Generation failed — check API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAllTiers = async () => {
    setGeneratingAllTiers(true);
    setAllTiersProgress('Generating all 5 tiers — this takes ~30 seconds…');
    try {
      const result = await adminService.generateAllTiers(allTiersForm);
      setShowAllTiersModal(false);
      setAllTiersProgress(null);
      const summary = Object.entries(result.tiers)
        .map(([d, r]) => `${d}: ${r.inserted}`)
        .join(', ');
      toast.success('All Tiers Generated', `${result.total_inserted} questions added as DRAFT (${summary})`);
      loadQuestions(qOffset, qFilters);
    } catch {
      setAllTiersProgress(null);
      toast.error('Error', 'Generation failed — check API key');
    } finally {
      setGeneratingAllTiers(false);
    }
  };

  const handleBulkApprove = async () => {
    setBulkApproving(true);
    try {
      const result = await adminService.bulkApproveQuestions(
        qFilters.category !== 'all' ? { category: qFilters.category } : undefined
      );
      toast.success('Approved', `${result.approved} questions are now LIVE`);
      loadQuestions(qOffset, qFilters);
    } catch {
      toast.error('Error', 'Bulk approve failed');
    } finally {
      setBulkApproving(false);
    }
  };

  const handleModerationAction = async (id: number, newStatus: 'live' | 'retired') => {
    setUpdatingId(id);
    try {
      await adminService.updateProQuestion(id, { status: newStatus });
      setQuestions(qs => qs.filter(q => q.id !== id));
      setQTotal(t => t - 1);
    } catch {
      toast.error('Error', 'Failed to update question status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSessionSearch = () => {
    setSFilters(sPendingFilters);
    loadSessions(0, sPendingFilters);
  };

  const handleImport = async () => {
    setImportResult(null);
    let parsed: object[];
    try {
      const raw = JSON.parse(importJson);
      parsed = Array.isArray(raw) ? raw : [raw];
    } catch (e: any) {
      setImportResult({ errors: [`Invalid JSON: ${e.message}`] });
      return;
    }
    setImporting(true);
    try {
      const result = await adminService.bulkImportProQuestions(parsed);
      setImportResult({ created: result.created });
      setImportJson('');
      // Refresh question count shown in tab
      loadQuestions(0, qFilters);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        setImportResult({ errors: detail });
      } else {
        setImportResult({ errors: [typeof detail === 'string' ? detail : 'Import failed'] });
      }
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = [
      {
        question_text: "What is the capital of France?",
        options: ["Paris", "London", "Berlin", "Madrid"],
        correct_answer: "Paris",
        category: "geography",
        difficulty: "easy",
        country_relevance: "universal",
        explanation: "Paris has been the capital of France since the 10th century.",
        topic: "European capitals"
      },
      {
        question_text: "What is the powerhouse of the cell?",
        options: ["Nucleus", "Mitochondria", "Ribosome", "Lysosome"],
        correct_answer: "Mitochondria",
        category: "science",
        difficulty: "hard",
        country_relevance: "universal",
        explanation: "Mitochondria generate most of the cell's supply of ATP.",
        topic: "Cell biology"
      }
    ];
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pro_questions_template.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filledOptions = createForm.options.filter(o => o.trim());

  return (
    <div className="pro-page config-page-modern">
      <div className="config-header">
        <h1><Brain size={24} /> Pro Workspace</h1>
        <p className="header-subtitle">Manage quiz questions and monitor Pro sessions</p>
      </div>

      <div className="config-tabs">
        <button className={activeTab === 'questions' ? 'active' : ''} onClick={() => setActiveTab('questions')}>
          Questions {qTotal > 0 && <span style={{ marginLeft: 4, opacity: 0.8 }}>({qTotal})</span>}
        </button>
        <button className={activeTab === 'sessions' ? 'active' : ''} onClick={() => setActiveTab('sessions')}>
          Sessions {sTotal > 0 && <span style={{ marginLeft: 4, opacity: 0.8 }}>({sTotal})</span>}
        </button>
        <button className={activeTab === 'import' ? 'active' : ''} onClick={() => setActiveTab('import')}>
          <Upload size={16} /> Import
        </button>
      </div>

      <div className="config-content">

        {/* ── QUESTIONS TAB ── */}
        {activeTab === 'questions' && (
          <>
            <div className="section-header">
              <h3>Question Bank</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="refresh-btn" onClick={() => loadQuestions(qOffset, qFilters)} disabled={qLoading}>
                  <RefreshCw size={16} className={qLoading ? 'spin' : ''} />
                  Refresh
                </button>
                <button className="refresh-btn" onClick={handleCalibrate} disabled={calibrating} title="Recalculate confidence scores and flag low-confidence questions">
                  <BarChart2 size={16} className={calibrating ? 'spin' : ''} />
                  {calibrating ? 'Calibrating…' : 'Calibrate'}
                </button>
                <button className="refresh-btn" onClick={() => setShowGenModal(true)} title="Generate questions for one difficulty tier">
                  <Sparkles size={16} />
                  Generate
                </button>
                <button className="refresh-btn" onClick={() => setShowAllTiersModal(true)} title="Generate questions for all 5 difficulty tiers at once" style={{ background: 'linear-gradient(135deg, #410C6D, #5a1494)', color: 'white', border: 'none' }}>
                  <Sparkles size={16} />
                  Generate All Tiers
                </button>
                <button className="refresh-btn" onClick={handleBulkApprove} disabled={bulkApproving} title="Approve all draft questions → Live" style={{ background: '#15803d', color: 'white', border: 'none' }}>
                  <Save size={16} className={bulkApproving ? 'spin' : ''} />
                  {bulkApproving ? 'Approving…' : 'Approve All Drafts'}
                </button>
                <button className="create-btn" onClick={() => setShowCreateForm(s => !s)}>
                  {showCreateForm ? <X size={16} /> : <Plus size={16} />}
                  {showCreateForm ? 'Cancel' : 'Add Question'}
                </button>
              </div>
            </div>

            {/* Review Queue shortcut */}
            <button
              className="review-queue-chip"
              onClick={() => { setQFilters(f => ({ ...f, status: 'review' })); setQOffset(0); loadQuestions(0, { ...qFilters, status: 'review' }); }}
            >
              <AlertTriangle size={14} /> Review Queue
            </button>

            {/* Filter bar */}
            <div className="pro-filter-bar">
              <select value={qFilters.category} onChange={e => handleQFilterChange('category', e.target.value)}>
                <option value="all">All categories</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
              </select>
              <select value={qFilters.difficulty} onChange={e => handleQFilterChange('difficulty', e.target.value)}>
                <option value="all">All difficulties</option>
                {DIFFICULTIES.map(d => <option key={d} value={d}>{d.replace('_', ' ')}</option>)}
              </select>
              <select value={qFilters.status} onChange={e => handleQFilterChange('status', e.target.value)}>
                <option value="all">All statuses</option>
                {Q_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Create form */}
            {showCreateForm && (
              <div className="pro-create-form">
                <h4 className="pro-create-title">New Question</h4>
                <div className="pro-create-grid">
                  <div className="pro-field full">
                    <label>Question text *</label>
                    <textarea
                      rows={3}
                      value={createForm.question_text}
                      onChange={e => setCreateForm(f => ({ ...f, question_text: e.target.value }))}
                      placeholder="Enter the question…"
                    />
                  </div>
                  {createForm.options.map((opt, i) => (
                    <div key={i} className="pro-field">
                      <label>Option {String.fromCharCode(65 + i)} *</label>
                      <input
                        type="text"
                        value={opt}
                        onChange={e => handleCreateFormOption(i, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + i)}`}
                      />
                    </div>
                  ))}
                  <div className="pro-field">
                    <label>Correct answer *</label>
                    <select value={createForm.correct_answer} onChange={e => setCreateForm(f => ({ ...f, correct_answer: e.target.value }))}>
                      <option value="">— select —</option>
                      {filledOptions.map((o, i) => <option key={i} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="pro-field">
                    <label>Category *</label>
                    <select value={createForm.category} onChange={e => setCreateForm(f => ({ ...f, category: e.target.value }))}>
                      <option value="">— select —</option>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div className="pro-field">
                    <label>Difficulty *</label>
                    <select value={createForm.difficulty} onChange={e => setCreateForm(f => ({ ...f, difficulty: e.target.value }))}>
                      <option value="">— select —</option>
                      {DIFFICULTIES.map(d => <option key={d} value={d}>{d.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div className="pro-field">
                    <label>Country relevance</label>
                    <select value={createForm.country_relevance} onChange={e => setCreateForm(f => ({ ...f, country_relevance: e.target.value }))}>
                      {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                    </select>
                  </div>
                  <div className="pro-field full">
                    <label>Explanation</label>
                    <textarea
                      rows={2}
                      value={createForm.explanation}
                      onChange={e => setCreateForm(f => ({ ...f, explanation: e.target.value }))}
                      placeholder="Optional explanation shown after answer…"
                    />
                  </div>
                  <div className="pro-field">
                    <label>Topic (optional)</label>
                    <input
                      type="text"
                      value={createForm.topic}
                      onChange={e => setCreateForm(f => ({ ...f, topic: e.target.value }))}
                      placeholder="e.g. World War II"
                    />
                  </div>
                </div>
                <div className="pro-create-actions">
                  <button
                    className="create-btn"
                    onClick={handleCreate}
                    disabled={creating || !isCreateValid()}
                  >
                    <Save size={16} />
                    {creating ? 'Saving…' : 'Save Question'}
                  </button>
                  <button className="refresh-btn" onClick={() => { setShowCreateForm(false); setCreateForm({ ...EMPTY_FORM, options: ['', '', '', ''] }); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Questions table */}
            {qLoading ? (
              <div className="loading-state"><RefreshCw size={24} className="spin" /><span>Loading questions…</span></div>
            ) : (
              <div className="table-container">
                <table className="config-table">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>ID</th>
                      <th>Question</th>
                      <th>Category</th>
                      <th>Difficulty</th>
                      <th>Status</th>
                      <th>Accuracy</th>
                      <th>Confidence</th>
                      <th>Topic</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.length === 0 ? (
                      <tr><td colSpan={9} className="empty-state">No questions found</td></tr>
                    ) : questions.map(q => (
                      <tr key={q.id} style={{ opacity: updatingId === q.id ? 0.5 : 1 }}>
                        <td style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{q.id}</td>
                        <td
                          title={q.question_text}
                          style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {q.question_text.length > 80 ? q.question_text.slice(0, 80) + '…' : q.question_text}
                        </td>
                        <td>
                          <span className="currency-badge">{q.category.replace('_', ' ')}</span>
                        </td>
                        <td>
                          <span className="difficulty-badge" style={DIFFICULTY_STYLES[q.difficulty] ?? {}}>
                            {q.difficulty.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          {q.status === 'review' ? (
                            <div className="mod-actions">
                              <button
                                className="mod-btn approve"
                                disabled={updatingId === q.id}
                                onClick={() => handleModerationAction(q.id, 'live')}
                              >
                                ✓ Approve
                              </button>
                              <button
                                className="mod-btn reject"
                                disabled={updatingId === q.id}
                                onClick={() => handleModerationAction(q.id, 'retired')}
                              >
                                ✕ Reject
                              </button>
                            </div>
                          ) : (
                            <select
                              className="inline-status-select"
                              value={q.status}
                              disabled={updatingId === q.id}
                              onChange={e => handleStatusChange(q.id, e.target.value)}
                            >
                              {Q_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="accuracy-cell">
                          {q.times_correct}/{q.times_answered}
                          <span className="accuracy-pct">
                            {q.times_answered > 0
                              ? ` (${Math.round((q.times_correct / q.times_answered) * 100)}%)`
                              : ' (—)'}
                          </span>
                        </td>
                        <td>
                          <span className="confidence-badge" style={getConfidenceStyle(q.confidence_score)}>
                            {q.confidence_score !== null
                              ? `${Math.round(q.confidence_score * 100)}%`
                              : '—'}
                          </span>
                        </td>
                        <td style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{q.topic ?? '—'}</td>
                        <td style={{ color: '#9ca3af', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {new Date(q.updated_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="pagination-bar">
                  <span>
                    {qTotal === 0 ? 'No results' : `${qOffset + 1}–${Math.min(qOffset + PAGE_SIZE, qTotal)} of ${qTotal}`}
                  </span>
                  <button className="pag-btn" disabled={qOffset === 0} onClick={() => loadQuestions(qOffset - PAGE_SIZE, qFilters)}>
                    ← Prev
                  </button>
                  <button className="pag-btn" disabled={qOffset + PAGE_SIZE >= qTotal} onClick={() => loadQuestions(qOffset + PAGE_SIZE, qFilters)}>
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── SESSIONS TAB ── */}
        {activeTab === 'sessions' && (
          <>
            <div className="section-header">
              <h3>Pro Sessions</h3>
              <button className="refresh-btn" onClick={() => loadSessions(sOffset, sFilters)} disabled={sLoading}>
                <RefreshCw size={16} className={sLoading ? 'spin' : ''} />
                Refresh
              </button>
            </div>

            <div className="pro-filter-bar">
              <select value={sPendingFilters.status} onChange={e => setSPendingFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="all">All statuses</option>
                {S_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input
                type="number"
                placeholder="User ID"
                value={sPendingFilters.user_id}
                style={{ width: 120 }}
                onChange={e => setSPendingFilters(f => ({ ...f, user_id: e.target.value }))}
              />
              <button className="create-btn" onClick={handleSessionSearch}>
                <Search size={16} /> Search
              </button>
            </div>

            {sLoading ? (
              <div className="loading-state"><RefreshCw size={24} className="spin" /><span>Loading sessions…</span></div>
            ) : (
              <div className="table-container">
                <table className="config-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Rung</th>
                      <th>TP Earned</th>
                      <th>Safe Haven TP</th>
                      <th>Started</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.length === 0 ? (
                      <tr><td colSpan={9} className="empty-state">No sessions found</td></tr>
                    ) : sessions.map(s => (
                      <tr key={s.id}>
                        <td style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{s.id}</td>
                        <td>
                          <a
                            href={`/dashboard/users?userId=${s.user_id}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#410C6D', textDecoration: 'none', fontWeight: 500 }}
                          >
                            #{s.user_id}
                          </a>
                        </td>
                        <td>
                          <span className="currency-badge">{s.category.replace('_', ' ')}</span>
                        </td>
                        <td>
                          <span
                            className="status-badge"
                            style={SESSION_STATUS_COLOURS[s.status] ?? {}}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{s.current_rung}</td>
                        <td style={{ fontWeight: 600, color: '#15803d' }}>{s.tp_earned.toLocaleString()} TP</td>
                        <td style={{ color: '#9ca3af' }}>{s.safe_haven_tp.toLocaleString()} TP</td>
                        <td style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{relativeTime(s.started_at)}</td>
                        <td style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{sessionDuration(s.started_at, s.ended_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="pagination-bar">
                  <span>
                    {sTotal === 0 ? 'No results' : `${sOffset + 1}–${Math.min(sOffset + PAGE_SIZE, sTotal)} of ${sTotal}`}
                  </span>
                  <button className="pag-btn" disabled={sOffset === 0} onClick={() => loadSessions(sOffset - PAGE_SIZE, sFilters)}>
                    ← Prev
                  </button>
                  <button className="pag-btn" disabled={sOffset + PAGE_SIZE >= sTotal} onClick={() => loadSessions(sOffset + PAGE_SIZE, sFilters)}>
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {/* ── IMPORT TAB ── */}
        {activeTab === 'import' && (
          <>
            <div className="section-header">
              <h3>Bulk Question Import</h3>
              <button className="refresh-btn" onClick={handleDownloadTemplate}>
                <Download size={16} /> Download template
              </button>
            </div>

            <div className="import-section">
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151' }}>
                Paste JSON array of questions
              </label>
              <textarea
                className="import-textarea"
                value={importJson}
                onChange={e => { setImportJson(e.target.value); setImportResult(null); }}
                placeholder={'[\n  {\n    "question_text": "...",\n    "options": ["A","B","C","D"],\n    "correct_answer": "A",\n    "category": "geography",\n    "difficulty": "easy",\n    ...\n  }\n]'}
                spellCheck={false}
              />

              {importResult && importResult.created !== undefined && (
                <div className="import-result success">
                  Successfully imported {importResult.created} question{importResult.created !== 1 ? 's' : ''} (status: draft).
                </div>
              )}
              {importResult?.errors && (
                <div className="import-result error">
                  <strong>Validation errors:</strong>
                  <ul style={{ margin: '6px 0 0 0', paddingLeft: 20 }}>
                    {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <button
                  className="create-btn"
                  onClick={handleImport}
                  disabled={importing || !importJson.trim()}
                >
                  <Upload size={16} />
                  {importing ? 'Importing…' : 'Validate & Import'}
                </button>
              </div>
            </div>

            <details className="format-guide">
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#374151', padding: '12px 0' }}>
                Format reference
              </summary>
              <pre>{`[
  {
    "question_text": "What is the capital of France?",
    "options": ["Paris", "London", "Berlin", "Madrid"],
    "correct_answer": "Paris",
    "category": "geography",
    "difficulty": "easy",
    "country_relevance": "universal",
    "explanation": "Paris has been the capital of France since...",
    "topic": "European capitals"
  }
]

Valid categories: science, politics, football, history, geography,
  entertainment, technology, general_knowledge, mixed

Valid difficulties: easy, easy_medium, medium_hard, hard, expert

Valid country_relevance: universal, ng, uk`}</pre>
            </details>
          </>
        )}

      </div>

      {showAllTiersModal && (
        <div className="modal-overlay" onClick={() => !generatingAllTiers && setShowAllTiersModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Generate All Tiers</h3>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Claude generates <strong>{allTiersForm.count_per_tier} questions × 5 difficulty tiers = {allTiersForm.count_per_tier * 5} questions</strong>, all added as DRAFT. Use <em>Approve All Drafts</em> after reviewing.
            </p>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={allTiersForm.category} onChange={e => setAllTiersForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.filter(c => c !== 'mixed').map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-select" value={allTiersForm.country_relevance} onChange={e => setAllTiersForm(f => ({ ...f, country_relevance: e.target.value }))}>
                {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Questions per tier (1–20)</label>
              <input
                type="number"
                className="form-input"
                min={1}
                max={20}
                value={allTiersForm.count_per_tier}
                onChange={e => setAllTiersForm(f => ({ ...f, count_per_tier: Math.min(20, Math.max(1, Number(e.target.value))) }))}
              />
            </div>
            {allTiersProgress && (
              <p style={{ color: '#5a1494', fontSize: '0.875rem', fontStyle: 'italic' }}>{allTiersProgress}</p>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAllTiersModal(false)} disabled={generatingAllTiers}>Cancel</button>
              <button className="btn-primary" onClick={handleGenerateAllTiers} disabled={generatingAllTiers}>
                {generatingAllTiers ? 'Generating all tiers…' : `Generate ${allTiersForm.count_per_tier * 5} questions`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenModal && (
        <div className="modal-overlay" onClick={() => !generating && setShowGenModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Generate Questions with AI</h3>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Questions are generated by Claude and added as DRAFT for review before publishing.
            </p>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={genForm.category} onChange={e => setGenForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Difficulty</label>
              <select className="form-select" value={genForm.difficulty} onChange={e => setGenForm(f => ({ ...f, difficulty: e.target.value }))}>
                {DIFFICULTIES.map(d => <option key={d} value={d}>{d.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-select" value={genForm.country_relevance} onChange={e => setGenForm(f => ({ ...f, country_relevance: e.target.value }))}>
                {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Count (1–20)</label>
              <input
                type="number"
                className="form-input"
                min={1}
                max={20}
                value={genForm.count}
                onChange={e => setGenForm(f => ({ ...f, count: Math.min(20, Math.max(1, Number(e.target.value))) }))}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowGenModal(false)} disabled={generating}>Cancel</button>
              <button className="btn-primary" onClick={handleGenerate} disabled={generating}>
                {generating ? 'Generating…' : `Generate ${genForm.count} questions`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pro;
